/**
 * Domain enums shared across API, web admin and mobile.
 *
 * **The values here are the values that travel over the wire**, which is not the
 * same thing as the values stored in PostgreSQL — and confusing the two is a
 * defect that compiles, runs, and quietly does nothing.
 *
 * Every enum below except `RoleCode` mirrors a native PostgreSQL enum declared
 * in `apps/api/prisma/schema.prisma`. Those declarations carry a `@map(...)` to
 * a lowercase code, and the `@map` is what lands in the database column. The
 * Prisma **client** — and therefore every JSON response of the API — uses the
 * enum *member name* instead: the row holds `auto_closed`, the response says
 * `AUTO_CLOSED`. A screen that compares a status against the mapped value finds
 * no match ever, on every row, and looks like an empty filter rather than a bug.
 *
 * So: `UPPER_SNAKE` names, `UPPER_SNAKE` values, always identical. If a member
 * name here differs from the Prisma one, `scripts/check-shared-enums.mjs` breaks
 * the build — that check exists because this file cannot be verified by reading
 * it, only by comparing it to something else.
 *
 * `RoleCode` is the single exception, and deliberately so: `Role.code` is a
 * plain `String` column pointing at the `roles` table, not a native enum, and
 * the codes really are lowercase. A company can add a role of its own, which is
 * exactly why that column is not an enum in the first place.
 */
/**
 * Global authorization roles **shipped with the product**.
 *
 * Not an exhaustive list of what can appear in `User.roleCode`: the `roles`
 * table is editable and a company-defined role is a perfectly legal value. Use
 * this to recognise the built-in ones, never to validate.
 */
export var RoleCode;
(function (RoleCode) {
    RoleCode["ADMIN"] = "admin";
    RoleCode["HR"] = "hr";
    RoleCode["MANAGER"] = "manager";
    RoleCode["EMPLOYEE"] = "employee";
    RoleCode["DRIVER"] = "driver";
    /**
     * Logistica — un solo account (Cosimo 2026-08-29): unisce il vecchio «gestore viaggi»
     * (anagrafiche: luoghi, mezzi, tipi di collo) e il vecchio «account responsabile» (operativo:
     * pianifica viaggi, gestisce spedizioni). I due erano separati e sono stati raggruppati; gli
     * utenti che li avevano sono migrati a questo.
     */
    RoleCode["LOGISTICA"] = "logistica";
    /**
     * Front-desk di una sede. Le sue letture sono **ristrette alla propria sede**
     * (`users.headquarter_id`): la reception di uno stabilimento vede le sale, i mezzi e — a mano
     * a mano che i domini vengono scopati — le altre cose solo del suo. Lo scope lo applica
     * `src/auth/site-scope.ts`, non il permesso: il permesso apre la porta, la sede la stringe.
     */
    RoleCode["RECEPTION"] = "reception";
    /**
     * Il banco che **consegna le divise**: vede le richieste di vestiario, le approva/rifiuta e
     * segna le consegne. Account dedicato — separato dalla reception e da HR — perché la consegna è
     * un mestiere a sé, di magazzino. `uniforms:manage`, senza scope di sede (un solo magazzino).
     */
    RoleCode["MAGAZZINO_DIVISE"] = "magazzino_divise";
    /**
     * L'amministrazione che **autorizza e liquida i rimborsi**. Deciso da Cosimo il 2026-09-06
     * (D21): «nasce un ruolo nuovo, "Note Spese" — non Risorse Umane, non un "Amministrazione"
     * generico: un ruolo suo». Le note spese escono dalla firma sull'app e il rimborso lo decide
     * questa figura dal Cruscotto.
     *
     * **È una persona, non un banco**, e la differenza si vede in `ROLE_PERMISSIONS`: parte da
     * `EMPLOYEE_BASE` come `hr`, e non da zero come `reception` e `magazzino_divise`. Il perché
     * per esteso sta là, sul pacchetto — qui basti che `role_code` è **una colonna sola**: chi
     * riceve questo ruolo non ne ha altri, e le persone a cui lo si darà sono gli otto
     * dell'AMMINISTRAZIONE, che timbrano, prendono ferie e hanno una busta paga.
     */
    RoleCode["NOTE_SPESE"] = "note_spese";
})(RoleCode || (RoleCode = {}));
/*
 * ------------------------------------------------------------------ anagrafiche
 *
 * The three lists below have `RoleCode`'s shape and `RoleCode`'s caveat: an
 * editable table, a `code` column that is a plain unique `String` and not a
 * native enum, and lowercase values. They are exempt in
 * `apps/api/scripts/check-shared-enums.mjs` for that reason, all four together.
 *
 * They exist for one job, and it is worth naming it because it is not
 * authorization: **the app translates the entries it ships with**. The `name`
 * column is written in Italian by the personnel office, which is right for the
 * people who share its language and useless to the ones who do not — and the
 * reason the app speaks Chinese at all is that a good part of the Prato
 * workforce reads it. So the app carries a phrase for each code it knows, and
 * falls back to the office's own words for the codes it does not.
 *
 * Use these to recognise the built-in entries, never to validate: a company that
 * adds a type of its own is using the product as designed.
 *
 * Where the phrases live and who is allowed to pick them: `src/lingua/anagrafiche.ts`
 * in the mobile app, and §63 of `spec/app-mobile.md`.
 */
/** Absence types seeded with the product (`justification_types.code`). */
export var JustificationTypeCode;
(function (JustificationTypeCode) {
    JustificationTypeCode["FERIE"] = "ferie";
    JustificationTypeCode["PERMESSO"] = "permesso";
    JustificationTypeCode["ROL"] = "rol";
    JustificationTypeCode["MALATTIA"] = "malattia";
    JustificationTypeCode["INFORTUNIO"] = "infortunio";
    JustificationTypeCode["CONGEDO"] = "congedo";
    JustificationTypeCode["LEGGE104"] = "legge104";
    JustificationTypeCode["TRASFERTA"] = "trasferta";
    JustificationTypeCode["SMARTWORKING"] = "smartworking";
})(JustificationTypeCode || (JustificationTypeCode = {}));
/** Expense-note types seeded with the product (`expense_note_types.code`). */
export var ExpenseNoteTypeCode;
(function (ExpenseNoteTypeCode) {
    ExpenseNoteTypeCode["VITTO"] = "vitto";
    ExpenseNoteTypeCode["ALLOGGIO"] = "alloggio";
    ExpenseNoteTypeCode["CARBURANTE"] = "carburante";
    ExpenseNoteTypeCode["PEDAGGI"] = "pedaggi";
    ExpenseNoteTypeCode["TRASPORTI"] = "trasporti";
    ExpenseNoteTypeCode["RAPPRESENTANZA"] = "rappresentanza";
    ExpenseNoteTypeCode["MATERIALI"] = "materiali";
    ExpenseNoteTypeCode["ALTRO"] = "altro";
})(ExpenseNoteTypeCode || (ExpenseNoteTypeCode = {}));
/** Package types seeded with the product (`transfer_package_types.code`). */
export var PackageTypeCode;
(function (PackageTypeCode) {
    PackageTypeCode["BANCALE"] = "bancale";
    PackageTypeCode["MEZZO_BANCALE"] = "mezzo_bancale";
    PackageTypeCode["SCATOLA"] = "scatola";
    PackageTypeCode["ROTOLO"] = "rotolo";
    PackageTypeCode["CARTELLA"] = "cartella";
    PackageTypeCode["BUSTA"] = "busta";
    PackageTypeCode["ALTRO"] = "altro";
})(PackageTypeCode || (PackageTypeCode = {}));
export var Gender;
(function (Gender) {
    Gender["MALE"] = "MALE";
    Gender["FEMALE"] = "FEMALE";
    Gender["OTHER"] = "OTHER";
    Gender["UNSPECIFIED"] = "UNSPECIFIED";
})(Gender || (Gender = {}));
/** Come una nota spese è stata pagata: contanti o carta aziendale. */
export var PaymentMethod;
(function (PaymentMethod) {
    PaymentMethod["CASH"] = "CASH";
    PaymentMethod["CARD"] = "CARD";
})(PaymentMethod || (PaymentMethod = {}));
/** Role of a user inside their department. */
export var DepartmentRole;
(function (DepartmentRole) {
    DepartmentRole["HEAD"] = "HEAD";
    DepartmentRole["DEPUTY"] = "DEPUTY";
    DepartmentRole["MEMBER"] = "MEMBER";
})(DepartmentRole || (DepartmentRole = {}));
export var DevicePlatform;
(function (DevicePlatform) {
    DevicePlatform["IOS"] = "IOS";
    DevicePlatform["ANDROID"] = "ANDROID";
    DevicePlatform["WEB"] = "WEB";
})(DevicePlatform || (DevicePlatform = {}));
// ---------------------------------------------------------------- time & leave
/** Where a work-time session was opened from. */
export var WorkTimeSource;
(function (WorkTimeSource) {
    WorkTimeSource["APP"] = "APP";
    WorkTimeSource["WEB"] = "WEB";
    WorkTimeSource["KIOSK"] = "KIOSK";
    WorkTimeSource["ADMIN"] = "ADMIN";
    /** Derivata dalle passate di badge del dispositivo (modello A): il lettore è la fonte. */
    WorkTimeSource["DEVICE"] = "DEVICE";
})(WorkTimeSource || (WorkTimeSource = {}));
export var WorkTimeStatus;
(function (WorkTimeStatus) {
    WorkTimeStatus["OPEN"] = "OPEN";
    WorkTimeStatus["CLOSED"] = "CLOSED";
    /** Closed by the nightly job because the employee forgot to clock out. */
    WorkTimeStatus["AUTO_CLOSED"] = "AUTO_CLOSED";
    WorkTimeStatus["CORRECTED"] = "CORRECTED";
    WorkTimeStatus["VOIDED"] = "VOIDED";
})(WorkTimeStatus || (WorkTimeStatus = {}));
/**
 * Where the minutes recognised for a contested day come from.
 *
 * Written by a person, never derived: the server knows the badge reader and the
 * timesheet disagree, it does not know which of the two actually happened. There is
 * no `PENDING` and no `IGNORED` on purpose — an open divergence is not a row of
 * `work_day_reviews` at all, it is a day that has no row.
 *
 * `NEITHER` is the case that looks like an oversight and is not: the reader saw her
 * pass and the timesheet says nothing because she was on leave that day. Neither
 * story is the working day, and the minutes are whatever the person signing writes.
 */
export var WorkDayVerdict;
(function (WorkDayVerdict) {
    WorkDayVerdict["READER"] = "READER";
    WorkDayVerdict["TIMESHEET"] = "TIMESHEET";
    WorkDayVerdict["NEITHER"] = "NEITHER";
})(WorkDayVerdict || (WorkDayVerdict = {}));
export var JustificationUnit;
(function (JustificationUnit) {
    JustificationUnit["DAY"] = "DAY";
    JustificationUnit["HOUR"] = "HOUR";
})(JustificationUnit || (JustificationUnit = {}));
/** Lifecycle of an absence/leave justification (two-level approval). */
export var JustificationStatus;
(function (JustificationStatus) {
    JustificationStatus["DRAFT"] = "DRAFT";
    JustificationStatus["PENDING_L1"] = "PENDING_L1";
    JustificationStatus["PENDING_L2"] = "PENDING_L2";
    JustificationStatus["APPROVED"] = "APPROVED";
    JustificationStatus["REJECTED"] = "REJECTED";
    JustificationStatus["CANCELLED"] = "CANCELLED";
    /**
     * Nessuno ha firmato, e il giorno è passato.
     *
     * ## Perché serviva un settimo stato, e non andava bene nessuno dei sei
     *
     * Cosimo, il 2026-10-13: «ci sono diverse richieste ferie vecchissime ancora in circolo
     * sul sistema. chiudi tutto che sono richieste antecedenti a oggi». Misurate quel giorno:
     * **28 in attesa, 10 con le date già passate** — due del giugno 2024, ferme da 847 e 776
     * giorni. (Le altre 18 non si toccano: hanno date future, fino alle ferie di Natale.)
     *
     * `REJECTED` avrebbe messo **un nome su una decisione che nessuno ha preso**: quello stato
     * porta `rejected_by_user_id`, `rejected_at` e il motivo, e la riga la rilegge la persona a
     * cui la richiesta è stata negata. `CANCELLED` avrebbe detto che se l'è ritirata lei, e non
     * è vero per nessuna delle dieci. Nessuno dei due è un'approssimazione innocua: sono due
     * frasi false su una pratica del personale.
     *
     * Non è nemmeno un'invenzione: il prodotto ha già questo concetto altrove, e lo chiama
     * così — `WorkTimeLogStatus.AUTO_CLOSED` per una giornata che nessuno ha chiuso,
     * `TransferStatus.EXPIRED` per una spedizione scaduta in coda. Qui mancava.
     */
    JustificationStatus["EXPIRED"] = "EXPIRED";
})(JustificationStatus || (JustificationStatus = {}));
/** Leave period proposed by HR, awaiting the employee's answer. */
export var ProposalStatus;
(function (ProposalStatus) {
    ProposalStatus["PENDING"] = "PENDING";
    ProposalStatus["APPROVED"] = "APPROVED";
    ProposalStatus["REJECTED"] = "REJECTED";
    ProposalStatus["CANCELLED"] = "CANCELLED";
})(ProposalStatus || (ProposalStatus = {}));
export var LeaveBalanceSource;
(function (LeaveBalanceSource) {
    LeaveBalanceSource["PAYROLL_IMPORT"] = "PAYROLL_IMPORT";
    LeaveBalanceSource["MANUAL"] = "MANUAL";
    LeaveBalanceSource["ACCRUAL"] = "ACCRUAL";
})(LeaveBalanceSource || (LeaveBalanceSource = {}));
/** Kind of payroll document. Replaces the legacy magic month numbers 13/14/99. */
export var PaycheckPeriodKind;
(function (PaycheckPeriodKind) {
    PaycheckPeriodKind["MONTHLY"] = "MONTHLY";
    PaycheckPeriodKind["THIRTEENTH"] = "THIRTEENTH";
    PaycheckPeriodKind["FOURTEENTH"] = "FOURTEENTH";
    PaycheckPeriodKind["BONUS"] = "BONUS";
    /** Certificazione Unica. */
    PaycheckPeriodKind["TAX_RETURN"] = "TAX_RETURN";
    /** TFR / cessazione. */
    PaycheckPeriodKind["SETTLEMENT"] = "SETTLEMENT";
})(PaycheckPeriodKind || (PaycheckPeriodKind = {}));
// ------------------------------------------------------------------- expenses
export var ExpenseNoteStatus;
(function (ExpenseNoteStatus) {
    ExpenseNoteStatus["DRAFT"] = "DRAFT";
    ExpenseNoteStatus["SUBMITTED"] = "SUBMITTED";
    ExpenseNoteStatus["APPROVED"] = "APPROVED";
    ExpenseNoteStatus["REJECTED"] = "REJECTED";
    ExpenseNoteStatus["REIMBURSED"] = "REIMBURSED";
})(ExpenseNoteStatus || (ExpenseNoteStatus = {}));
export var BusinessTripStatus;
(function (BusinessTripStatus) {
    BusinessTripStatus["PLANNED"] = "PLANNED";
    BusinessTripStatus["ONGOING"] = "ONGOING";
    BusinessTripStatus["COMPLETED"] = "COMPLETED";
    BusinessTripStatus["CANCELLED"] = "CANCELLED";
})(BusinessTripStatus || (BusinessTripStatus = {}));
// ---------------------------------------------------------------------- fleet
export var VehicleType;
(function (VehicleType) {
    VehicleType["CAR"] = "CAR";
    VehicleType["VAN"] = "VAN";
    VehicleType["TRUCK"] = "TRUCK";
    VehicleType["MOTORCYCLE"] = "MOTORCYCLE";
    VehicleType["FORKLIFT"] = "FORKLIFT";
    VehicleType["OTHER"] = "OTHER";
})(VehicleType || (VehicleType = {}));
export var FuelType;
(function (FuelType) {
    FuelType["PETROL"] = "PETROL";
    FuelType["DIESEL"] = "DIESEL";
    FuelType["ELECTRIC"] = "ELECTRIC";
    FuelType["HYBRID"] = "HYBRID";
    FuelType["LPG"] = "LPG";
    FuelType["METHANE"] = "METHANE";
    FuelType["OTHER"] = "OTHER";
})(FuelType || (FuelType = {}));
export var VehicleAvailability;
(function (VehicleAvailability) {
    VehicleAvailability["AVAILABLE"] = "AVAILABLE";
    VehicleAvailability["IN_USE"] = "IN_USE";
    VehicleAvailability["MAINTENANCE"] = "MAINTENANCE";
    VehicleAvailability["DISMISSED"] = "DISMISSED";
})(VehicleAvailability || (VehicleAvailability = {}));
/** A vehicle can serve staff booking, logistics, or both. */
export var VehicleUsage;
(function (VehicleUsage) {
    VehicleUsage["BOOKING"] = "BOOKING";
    VehicleUsage["LOGISTICS"] = "LOGISTICS";
})(VehicleUsage || (VehicleUsage = {}));
// ------------------------------------------------------------------- calendar
export var CalendarEventType;
(function (CalendarEventType) {
    CalendarEventType["GENERIC"] = "GENERIC";
    CalendarEventType["MEETING"] = "MEETING";
    CalendarEventType["VEHICLE"] = "VEHICLE";
    CalendarEventType["JUSTIFICATION"] = "JUSTIFICATION";
    CalendarEventType["BUSINESS_TRIP"] = "BUSINESS_TRIP";
    CalendarEventType["TRAINING"] = "TRAINING";
    CalendarEventType["HOLIDAY"] = "HOLIDAY";
})(CalendarEventType || (CalendarEventType = {}));
export var ParticipantStatus;
(function (ParticipantStatus) {
    ParticipantStatus["INVITED"] = "INVITED";
    ParticipantStatus["ACCEPTED"] = "ACCEPTED";
    ParticipantStatus["DECLINED"] = "DECLINED";
    ParticipantStatus["TENTATIVE"] = "TENTATIVE";
})(ParticipantStatus || (ParticipantStatus = {}));
// --------------------------------------------------- free/busy (on the device)
/** What kind of "busy" a reported interval is. Never a title, only a shape. */
export var FreeBusyKind;
(function (FreeBusyKind) {
    FreeBusyKind["BUSY"] = "BUSY";
    FreeBusyKind["TENTATIVE"] = "TENTATIVE";
    FreeBusyKind["OUT_OF_OFFICE"] = "OUT_OF_OFFICE";
    FreeBusyKind["WORKING_ELSEWHERE"] = "WORKING_ELSEWHERE";
})(FreeBusyKind || (FreeBusyKind = {}));
/** Where a busy interval came from. DEVICE ones are read on the phone, never by the server. */
export var FreeBusySource;
(function (FreeBusySource) {
    FreeBusySource["DEVICE"] = "DEVICE";
    FreeBusySource["INTERNAL"] = "INTERNAL";
    FreeBusySource["MANUAL"] = "MANUAL";
})(FreeBusySource || (FreeBusySource = {}));
/**
 * Why a person stopped reporting availability: a code, so the sentence can be written
 * in the reader's language instead of the server's (§72).
 *
 * Two members, and each has a writer: `CALENDAR_ACCESS_DENIED` the app declares when the
 * OS calendar permission is taken away, `DEVICE_REVOKED` the server writes when the
 * elected phone is logged out. Only the first may arrive from a client — a phone cannot
 * claim its own device was revoked.
 */
export var FreeBusySuspension;
(function (FreeBusySuspension) {
    FreeBusySuspension["CALENDAR_ACCESS_DENIED"] = "CALENDAR_ACCESS_DENIED";
    FreeBusySuspension["DEVICE_REVOKED"] = "DEVICE_REVOKED";
})(FreeBusySuspension || (FreeBusySuspension = {}));
/** Lifecycle of an event BesteHR asks the phone to write. SUSPENDED = permission revoked. */
export var CalendarOutboxStatus;
(function (CalendarOutboxStatus) {
    CalendarOutboxStatus["PENDING"] = "PENDING";
    CalendarOutboxStatus["APPLIED"] = "APPLIED";
    CalendarOutboxStatus["FAILED"] = "FAILED";
    CalendarOutboxStatus["SUSPENDED"] = "SUSPENDED";
})(CalendarOutboxStatus || (CalendarOutboxStatus = {}));
/**
 * What became of the `.ics` mailed to a participant who is not an employee.
 *
 * Twin of `CalendarOutboxStatus`, deliberately not the same enum: that one is about a
 * phone writing into a calendar of ours, this one about an envelope handed to someone
 * else's mail server. `SENT` means the mail server took it, not that anyone read it —
 * the invite is one-way by design (`RSVP=FALSE`), so no status here may ever claim to
 * know more than that.
 */
export var GuestInviteStatus;
(function (GuestInviteStatus) {
    GuestInviteStatus["PENDING"] = "PENDING";
    GuestInviteStatus["SENT"] = "SENT";
    GuestInviteStatus["CANCELLED"] = "CANCELLED";
    GuestInviteStatus["FAILED"] = "FAILED";
})(GuestInviteStatus || (GuestInviteStatus = {}));
// ------------------------------------------------------------------ logistics
export var TransferKind;
(function (TransferKind) {
    TransferKind["ORDINARY"] = "ORDINARY";
    TransferKind["EXTRAORDINARY"] = "EXTRAORDINARY";
})(TransferKind || (TransferKind = {}));
/** Lifecycle of a shipment. */
export var TransferStatus;
(function (TransferStatus) {
    TransferStatus["DRAFT"] = "DRAFT";
    TransferStatus["PENDING"] = "PENDING";
    TransferStatus["ASSIGNED"] = "ASSIGNED";
    TransferStatus["IN_PROGRESS"] = "IN_PROGRESS";
    TransferStatus["COMPLETED"] = "COMPLETED";
    TransferStatus["EXPIRED"] = "EXPIRED";
    TransferStatus["CANCELLED"] = "CANCELLED";
})(TransferStatus || (TransferStatus = {}));
/** Lifecycle of a driver's route. */
export var TripStatus;
(function (TripStatus) {
    TripStatus["PLANNED"] = "PLANNED";
    TripStatus["IN_PROGRESS"] = "IN_PROGRESS";
    TripStatus["COMPLETED"] = "COMPLETED";
    TripStatus["EXPIRED"] = "EXPIRED";
    TripStatus["CANCELLED"] = "CANCELLED";
})(TripStatus || (TripStatus = {}));
/**
 * What happened to a shipment without anybody deciding it: it timed out in the
 * queue, it came back from a route that ended without it being delivered, or —
 * `AUTO_COMPLETED` — it was picked up and nobody ever closed it, so after the
 * configured number of days the system recorded it as delivered.
 *
 * L'ultimo non è una scadenza travestita, ed è la ragione per cui è un valore suo: le altre
 * quattro dicono che la merce non è andata da nessuna parte, questo dice che è partita e
 * quasi certamente arrivata — manca la spunta, non il viaggio. Confonderli vorrebbe dire non
 * poter più rispondere a «quante ne chiudiamo davvero, e quante le chiude la macchina».
 *
 * These used to be typed by the server into `transfers.note` — the box a person
 * fills in and another person reads back — as `[non consegnata] caricata sul
 * viaggio VG-000012, che è scaduto senza chiusura`: an interface label, in
 * Italian, inside a data column, in a product that ships in three languages.
 * They are rows now, and the sentence is composed by whoever is reading, in the
 * language they are reading in.
 */
export var TransferEventKind;
(function (TransferEventKind) {
    TransferEventKind["EXPIRED_IN_QUEUE"] = "EXPIRED_IN_QUEUE";
    TransferEventKind["EXPIRED_AS_DRAFT"] = "EXPIRED_AS_DRAFT";
    TransferEventKind["RETURNED_UNLOADED"] = "RETURNED_UNLOADED";
    TransferEventKind["RETURNED_UNDELIVERED"] = "RETURNED_UNDELIVERED";
    TransferEventKind["AUTO_COMPLETED"] = "AUTO_COMPLETED";
})(TransferEventKind || (TransferEventKind = {}));
export var TripStopType;
(function (TripStopType) {
    TripStopType["START"] = "START";
    TripStopType["VIA"] = "VIA";
    TripStopType["END"] = "END";
})(TripStopType || (TripStopType = {}));
export var StopLinkRole;
(function (StopLinkRole) {
    StopLinkRole["PICKUP"] = "PICKUP";
    StopLinkRole["DELIVERY"] = "DELIVERY";
})(StopLinkRole || (StopLinkRole = {}));
/**
 * How long a shipment can wait. The legacy scale (an int 1..4) was good — every step
 * names a deadline instead of an adjective — but the words lived in a web bundle, so
 * the app could never show them. Here the words travel with the enum.
 */
export var TransferPriority;
(function (TransferPriority) {
    TransferPriority["HIGH"] = "HIGH";
    TransferPriority["MODERATE"] = "MODERATE";
    TransferPriority["MEDIUM"] = "MEDIUM";
    TransferPriority["LOW"] = "LOW";
})(TransferPriority || (TransferPriority = {}));
// ------------------------------------------------------------------- uniforms
export var UniformRequestStatus;
(function (UniformRequestStatus) {
    UniformRequestStatus["PENDING"] = "PENDING";
    UniformRequestStatus["APPROVED"] = "APPROVED";
    UniformRequestStatus["REJECTED"] = "REJECTED";
    UniformRequestStatus["DELIVERED"] = "DELIVERED";
    UniformRequestStatus["CANCELLED"] = "CANCELLED";
})(UniformRequestStatus || (UniformRequestStatus = {}));
// ----------------------------------------------------------- chat & messaging
export var ConversationType;
(function (ConversationType) {
    ConversationType["DIRECT"] = "DIRECT";
    ConversationType["GROUP"] = "GROUP";
    ConversationType["DEPARTMENT"] = "DEPARTMENT";
})(ConversationType || (ConversationType = {}));
/**
 * Perché un messaggio di chat viene segnalato (A8, richiesto dagli store).
 *
 * Quattro voci e non un campo libero: chi segnala di solito è agitato, e una casella vuota
 * davanti a un insulto produce o una riga inutile o nessuna segnalazione. Le parole con cui
 * si presentano stanno nei dizionari dell'app; qui c'è solo il nome che viaggia in rete.
 */
export var MessageReportReason;
(function (MessageReportReason) {
    MessageReportReason["OFFENSIVE"] = "OFFENSIVE";
    MessageReportReason["CONFIDENTIAL"] = "CONFIDENTIAL";
    MessageReportReason["OFF_TOPIC"] = "OFF_TOPIC";
    MessageReportReason["OTHER"] = "OTHER";
})(MessageReportReason || (MessageReportReason = {}));
export var NotificationType;
(function (NotificationType) {
    NotificationType["GENERIC"] = "GENERIC";
    NotificationType["CHAT_MESSAGE"] = "CHAT_MESSAGE";
    NotificationType["JUSTIFICATION"] = "JUSTIFICATION";
    NotificationType["PROPOSAL"] = "PROPOSAL";
    NotificationType["EXPENSE"] = "EXPENSE";
    NotificationType["BOOKING"] = "BOOKING";
    NotificationType["UNIFORM"] = "UNIFORM";
    NotificationType["PAYCHECK"] = "PAYCHECK";
    NotificationType["TRANSFER"] = "TRANSFER";
    NotificationType["TRIP"] = "TRIP";
    NotificationType["SHIFT"] = "SHIFT";
    NotificationType["CANTEEN"] = "CANTEEN";
    NotificationType["SYSTEM"] = "SYSTEM";
})(NotificationType || (NotificationType = {}));
/**
 * I tre turni standard più il personalizzato. Gemello di `ShiftKind` nello schema Prisma;
 * `check-shared-enums.mjs` pretende che i due file concordino. Gli orari dei tre standard non
 * stanno qui nel *nome* ma in `TURNO.orari` (`api.ts`): il nome dice quale turno, la costante
 * dice a che ora comincia e finisce.
 */
export var ShiftKind;
(function (ShiftKind) {
    ShiftKind["MORNING"] = "MORNING";
    ShiftKind["AFTERNOON"] = "AFTERNOON";
    ShiftKind["NIGHT"] = "NIGHT";
    ShiftKind["CUSTOM"] = "CUSTOM";
})(ShiftKind || (ShiftKind = {}));
/**
 * I sei criteri di valutazione delle prestazioni (D43). Gemello di `Criterio` nello schema
 * Prisma; `check-shared-enums.mjs` pretende che i due file concordino. L'ordine e le etichette
 * leggibili non stanno qui ma in `VALUTAZIONE` (`api.ts`): qui c'è solo la lista chiusa dei nomi.
 */
export var Criterio;
(function (Criterio) {
    Criterio["QUALITA"] = "QUALITA";
    Criterio["PRODUTTIVITA"] = "PRODUTTIVITA";
    Criterio["DISPONIBILITA"] = "DISPONIBILITA";
    Criterio["POLIVALENZA"] = "POLIVALENZA";
    Criterio["COLLABORAZIONE"] = "COLLABORAZIONE";
    Criterio["AFFIDABILITA"] = "AFFIDABILITA";
})(Criterio || (Criterio = {}));
/**
 * How `Notification.body` is to be read. Declared here — not in `comunicato.ts`,
 * where the grammar it names lives — because the Prisma schema declares the twin
 * enum and `check-shared-enums.mjs` requires the two files to agree.
 */
export var NotificationBodyFormat;
(function (NotificationBodyFormat) {
    /** Plain text, no syntax: the format of everything that exists today. */
    NotificationBodyFormat["PLAIN"] = "PLAIN";
    /** The restricted markdown of `comunicato.ts`. */
    NotificationBodyFormat["COMUNICATO"] = "COMUNICATO";
})(NotificationBodyFormat || (NotificationBodyFormat = {}));
/**
 * Chi ha scritto una traduzione: la macchina o una persona.
 *
 * Sta qui e non in `traduzione.ts`, dove vivono le regole che la usano, per lo stesso motivo
 * scritto sopra `NotificationBodyFormat`: lo schema Prisma dichiara il gemello e
 * `check-shared-enums.mjs` pretende che i due file dicano la stessa cosa. `traduzione.ts` la
 * riesporta come tipo (`OrigineTraduzione`) invece di riscrivere l'unione — una copia che non
 * c'è non può invecchiare (§60).
 *
 * È la regola (b) di K75 messa in un dato: il testo automatico **si dichiara** tale, e chi legge
 * lo sa senza doverlo indovinare dalla qualità della frase.
 */
export var TranslationOrigin;
(function (TranslationOrigin) {
    TranslationOrigin["AUTOMATICA"] = "AUTOMATICA";
    TranslationOrigin["MANO"] = "MANO";
    /**
     * Resa da un modello scritto a mano in tutte e tre le lingue (`notifiche-modelli.ts`): la
     * macchina non l'ha toccata, e nemmeno una persona l'ha corretta dopo. Il terzo valore esiste
     * perché senza di lui questi testi erano marcati `AUTOMATICA` — che dice il falso, che un
     * traduttore statistico li ha prodotti — e chi legge si vedeva l'avviso «tradotta a macchina»
     * su una frase scritta apposta a mano. Debito §7 chiuso il 2026-09-07; nasce con K205.
     */
    TranslationOrigin["MODELLO"] = "MODELLO";
})(TranslationOrigin || (TranslationOrigin = {}));
/**
 * A che punto è una traduzione.
 *
 * `FALLITA` è uno stato dichiarato e non l'assenza della riga: una traduzione che non c'è e una
 * che non si è riusciti a fare sono due fatti diversi, e senza un nome per il secondo diventano
 * indistinguibili — che è precisamente il modo in cui una coda smette di funzionare senza che
 * nessuno se ne accorga.
 */
export var TranslationStatus;
(function (TranslationStatus) {
    TranslationStatus["IN_ATTESA"] = "IN_ATTESA";
    TranslationStatus["PRONTA"] = "PRONTA";
    TranslationStatus["FALLITA"] = "FALLITA";
})(TranslationStatus || (TranslationStatus = {}));
/**
 * Lo stato di un ordine della mensa.
 *
 * Ce ne sono **due** per persona e per giorno, al massimo: una `BOZZA` e un `CONFERMATO`.
 * Confermare promuove la bozza e butta il confermato che c'era.
 *
 * Sembra più complicato di uno stato solo, e l'alternativa è peggiore: con una riga sola, chi
 * riapre un ordine confermato per cambiare una quantità e poi si distrae lo lascerebbe in
 * bozza — e alle 09:45, quando le bozze si buttano, **perderebbe il pranzo per aver provato a
 * modificarlo**. Con due righe, una modifica non confermata non tocca quello che è già stato
 * ordinato. È `spec/mensa.md` §3 D13.
 */
export var CanteenOrderStatus;
(function (CanteenOrderStatus) {
    CanteenOrderStatus["BOZZA"] = "BOZZA";
    CanteenOrderStatus["CONFERMATO"] = "CONFERMATO";
})(CanteenOrderStatus || (CanteenOrderStatus = {}));
/**
 * Perché una persona che legge in un'altra lingua si è trovata davanti l'italiano.
 *
 * I tre valori sono i tre modi in cui una traduzione può non esserci al momento in cui
 * qualcuno apre: non è ancora stata fatta, è stata fatta da un testo che nel frattempo è
 * cambiato, oppure la macchina ci ha rinunciato. Non è un dettaglio da schermata: **si
 * scrive in tabella**, su `notification_recipients.read_fallback`, perché aprire una
 * circolare che non si può leggere e leggerla sono due fatti diversi, e senza un nome per
 * il primo la casella «informato» si chiude per tutti e due (K75).
 *
 * Sta qui e non in `traduzione.ts` per la stessa ragione delle due sopra: lo schema Prisma
 * dichiara il gemello e `check-shared-enums.mjs` pretende che i due file dicano la stessa
 * cosa. `traduzione.ts` la riesporta dentro `MotivoRipiego` invece di riscrivere l'unione.
 */
export var TranslationFallback;
(function (TranslationFallback) {
    TranslationFallback["NON_ANCORA_TRADOTTA"] = "NON_ANCORA_TRADOTTA";
    TranslationFallback["TRADUZIONE_SCADUTA"] = "TRADUZIONE_SCADUTA";
    TranslationFallback["TRADUZIONE_NON_RIUSCITA"] = "TRADUZIONE_NON_RIUSCITA";
})(TranslationFallback || (TranslationFallback = {}));
export var PushStatus;
(function (PushStatus) {
    PushStatus["QUEUED"] = "QUEUED";
    PushStatus["SENT"] = "SENT";
    PushStatus["DELIVERED"] = "DELIVERED";
    PushStatus["FAILED"] = "FAILED";
    PushStatus["SKIPPED"] = "SKIPPED";
})(PushStatus || (PushStatus = {}));
export var PushTransport;
(function (PushTransport) {
    PushTransport["WEBSOCKET"] = "WEBSOCKET";
    PushTransport["UNIFIEDPUSH"] = "UNIFIEDPUSH";
    PushTransport["APNS"] = "APNS";
    PushTransport["FCM"] = "FCM";
})(PushTransport || (PushTransport = {}));
// --------------------------------------------------------------------- system
export var AuditAction;
(function (AuditAction) {
    AuditAction["CREATE"] = "CREATE";
    AuditAction["UPDATE"] = "UPDATE";
    AuditAction["DELETE"] = "DELETE";
    AuditAction["LOGIN"] = "LOGIN";
    AuditAction["LOGIN_FAILED"] = "LOGIN_FAILED";
    AuditAction["LOGOUT"] = "LOGOUT";
    AuditAction["APPROVE"] = "APPROVE";
    AuditAction["REJECT"] = "REJECT";
    AuditAction["DOWNLOAD"] = "DOWNLOAD";
    AuditAction["EXPORT"] = "EXPORT";
    /**
     * Aperta e letta, senza che niente cambiasse.
     *
     * Sta a parte da `DOWNLOAD` perché non esce nessun file: è la consultazione di una scheda.
     * Serve all'archivio dei cessati, dove restringere chi può guardare senza registrare chi ha
     * guardato renderebbe la restrizione valida quanto l'elenco dei permessi — cioè verificabile
     * solo finché nessuno lo cambia. Non si scrive per ogni elenco sfogliato: solo per l'apertura
     * del fascicolo di una persona, che è il gesto che espone codice fiscale, data di nascita e
     * recapiti privati.
     */
    AuditAction["CONSULT"] = "CONSULT";
    AuditAction["PASSWORD_CHANGE"] = "PASSWORD_CHANGE";
    AuditAction["IMPERSONATE"] = "IMPERSONATE";
})(AuditAction || (AuditAction = {}));
export var JobRunStatus;
(function (JobRunStatus) {
    JobRunStatus["RUNNING"] = "RUNNING";
    JobRunStatus["SUCCESS"] = "SUCCESS";
    JobRunStatus["FAILED"] = "FAILED";
})(JobRunStatus || (JobRunStatus = {}));
//# sourceMappingURL=enums.js.map